import { getConfig } from '@klpkonsern/appconfig';
import { PROSJEKT_TYPE } from '@klp/formgenerator/interfaces';
import i18n from 'i18next';
import { getBrukerDisplayName } from '@klp/user/utils';
import type {
    GodkjenningProsessBase,
    GodkjenningProsessResponse,
} from '../interfaces';

const safelyParseFloat = (stringValue: string | undefined) => {
    if (!stringValue) return 0;

    try {
        const parsed = parseFloat(stringValue);
        return isNaN(parsed) ? 0 : parsed;
    } catch {
        return 0;
    }
};

const getProjectTypeKey = (type?: PROSJEKT_TYPE | string) => {
    switch (type) {
        case PROSJEKT_TYPE.LTT:
            return 'project.types.LTT';
        case PROSJEKT_TYPE.TEK:
            return 'project.types.TEK';
        case PROSJEKT_TYPE.KOM:
            return 'project.types.KOM';
        case PROSJEKT_TYPE.TILTAK:
            return 'project.types.TILTAK';
        default:
            return 'project.types.Unknown';
    }
};

const getProjectTypeKeyAbbreviation = (type?: PROSJEKT_TYPE | string) => {
    switch (type) {
        case PROSJEKT_TYPE.LTT:
            return 'project.types.LTT_Abbreviation';
        case PROSJEKT_TYPE.TEK:
            return 'project.types.TEK_Abbreviation';
        case PROSJEKT_TYPE.KOM:
            return 'project.types.KOM_Abbreviation';
        case PROSJEKT_TYPE.TILTAK:
            return 'project.types.TILTAK';
        default:
            return 'project.types.Unknown';
    }
};

/**
 * Translate a ProsjektSkjemaType name with fallback support.
 * Attempts to map the type name to a translation key.
 * If no translation exists, falls back to the raw value from backend.
 *
 * @param typeName - The type name from backend (e.g., "Hovedprosjekt", "Underprosjekt")
 * @returns The translated label or the original typeName as fallback
 */
const translateProsjektSkjemaType = (typeName?: string): string => {
    if (!typeName) {
        return '';
    }

    const normalizedName = typeName.toLowerCase();
    const translationKey = `project.opprettProsjekt.prosjektskjematype.${normalizedName}`;

    const translation = i18n.t(translationKey);

    if (translation && translation !== translationKey) {
        return translation;
    }

    return typeName;
};

const getSuperofficeProsjektUrl = () =>
    getConfig('WEBAPP_APP_SUPEROFFICE_PROSJEKT_LINK');

const getSuperofficeProsjektUrlByProsjektId = (prosjektId: string) =>
    `${getSuperofficeProsjektUrl()}?project_id=${prosjektId}`;

// Role fields on the process whose name differs from the role (kandidatGruppeEier)
const roleToBrukerIdField: Record<string, string> = {
    Administrerendedirektor: 'administrerendesdirektor',
};

/**
 * Returns the brukerId of the person holding the given role on the process, by reading
 * the process field named after the role (e.g. Prosjektleder -> prosjektleder).
 * Returns undefined when the process does not expose an id for that role.
 */
const getRoleHolderBrukerId = (
    prosjekt: GodkjenningProsessBase,
    kandidatGruppeEier: string | null | undefined,
): string | undefined => {
    if (!kandidatGruppeEier) return undefined;

    const field =
        roleToBrukerIdField[kandidatGruppeEier] ??
        kandidatGruppeEier.charAt(0).toLowerCase() +
            kandidatGruppeEier.slice(1);
    const value = (prosjekt as unknown as Record<string, unknown>)[field];

    return typeof value === 'string' && value ? value : undefined;
};

const getActivityAnsvarlig = (
    prosjekt: GodkjenningProsessResponse,
    kandidatGruppeEier: string,
): string | undefined => {
    const roleToFulltnavn: Record<string, string | undefined> = {
        Prosjekteier: prosjekt.prosjekteierFulltnavn,
        Prosjektleder: prosjekt.prosjektlederFulltnavn,
        Prosjektkontroller: prosjekt.prosjektkontrollerFulltnavn,
        Okonomi: prosjekt.okonomiFulltnavn,
        Forvaltningsdirektor: prosjekt.forvaltningsdirektorFulltnavn,
        Utviklingsdirektor: prosjekt.utviklingsdirektorFulltnavn,
        Regiondirektor: prosjekt.regiondirektorFulltnavn,
        Administrerendedirektor: prosjekt.administrerendedirektorFulltnavn,
        Prosjektdirektor: prosjekt.prosjektdirektorFulltnavn,
        Teknisksjef: prosjekt.teknisksjefFulltnavn,
        Eiendomssjef: prosjekt.eiendomssjefFulltnavn,
    };
    return (
        getBrukerDisplayName(
            roleToFulltnavn[kandidatGruppeEier],
            getRoleHolderBrukerId(prosjekt, kandidatGruppeEier),
        ) || undefined
    );
};

export {
    getProjectTypeKey,
    getProjectTypeKeyAbbreviation,
    getSuperofficeProsjektUrl,
    getSuperofficeProsjektUrlByProsjektId,
    translateProsjektSkjemaType,
    safelyParseFloat,
    getActivityAnsvarlig,
    getRoleHolderBrukerId,
};
